import { MEDICATION_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { Medication } from "~/entities/medical/medication";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

export interface MedicationListingItem {
    id: string;
    title: string;
    prescribed: boolean;
    current: boolean;
}

const type = MedicalType.medication;

export default function MedicationsIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: MedicationListingItem[] = useMemo(() => {
        return listMedical(type).map((m: Medication) => ({
            id: m.id as string,
            title: findOption(OptionType.medications, m.option_id)?.title as string,
            prescribed: m.prescribed,
            current: m.current,
        }));
    }, [findOption, listMedical]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="medications">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share medications" initial={["medications", "photos"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Medications"
                    icon="medication"
                    button={() => (
                        <Can ability="update" section="medications">
                            <Button
                                title="Add medication"
                                variant="primary"
                                size="lg"
                                href="/health-profile/medications/create"
                            />
                        </Can>
                    )}
                >
                    Track your prescribed and over-the-counter medications, to stay on top of your treatment plan.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<MedicationListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        groups={[
                            { name: "Current", matches: (item) => item.current },
                            { name: "Past", matches: (item) => !item.current },
                        ]}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                hideChevron
                                badge={item.prescribed ? "Prescribed" : null}
                                href={{ pathname: `${MEDICATION_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
